/* eslint-disable no-undef */
/* eslint-disable react/no-multi-comp */
import React, { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useBlocker, useNavigate, useLocation, useParams } from 'react-router';
import { isFunction } from 'lodash';

import locationQuery from './locationQuery';
import { ROUTES } from './jsUi';

import { isLoggedIn } from '../state_managers/AuthenticationStateManager';

const routeLeaveHookPropTypes = {
  setRouterWillLeaveValidator: PropTypes.func,
  retryTransition: PropTypes.func,
};

export { routeLeaveHookPropTypes };

// react-router v8 removed `router.setRouteLeaveHook`; the equivalent is the
// `useBlocker` hook. This reimplements the previous class HOC as a functional
// one, preserving the API the wrapped components use (setRouterWillLeaveValidator,
// setRootComponentLeaveValidator, registerUnmountCleanupMethod, retryTransition).
//
// NOTE: leave-guard/blocker semantics are subtle and differ from v3; the
// unsaved-changes flows need runtime verification.
// Hook form of the route-leave behavior, usable directly from function
// components (e.g. HoldReasonRenderedAsset). The HOC below is a thin wrapper
// that spreads the returned handlers as props for legacy class components.
export function useRouteLeaveHook() {
  const navigate = useNavigate();
  const rawLocation = useLocation();
  const params = useParams();

  // react-router v8 dropped `location.query`; restore it (parsed from `search`)
  // so legacy class components reading `this.props.location.query` keep working.
  const location = useMemo(
    () => ({ ...rawLocation, query: locationQuery(rawLocation) }),
    [rawLocation],
  );

  const routerWillLeaveValidator = useRef<(nextRoute: any) => boolean>(() => true);
  const rootComponentLeaveValidator = useRef<((nextRoute: any) => any) | null>(null);
  const unmountCleanupMethods = useRef<Array<(nextRoute?: any) => void>>([]);

  const setRouterWillLeaveValidator = (fn: Function) => {
    if (isFunction(fn)) { routerWillLeaveValidator.current = fn as any; }
  };
  const setRootComponentLeaveValidator = (fn: Function) => {
    if (isFunction(fn)) { rootComponentLeaveValidator.current = fn as any; }
  };
  const registerUnmountCleanupMethod = (fn: Function) => {
    if (isFunction(fn)) { unmountCleanupMethods.current.push(fn as any); }
  };

  // useBlocker's callback returns `true` to BLOCK navigation (the inverse of
  // v3's routerWillLeave, which returned `true` to allow it).
  const blocker = useBlocker(({ nextLocation }: any) => {
    const nextRoute = { pathname: nextLocation.pathname };

    // Never block leaving to login / when logged out.
    if (nextRoute.pathname === `/${ROUTES.LOGIN}` || !isLoggedIn()) {
      return false;
    }

    // Root component opted to skip the reset process.
    if (
      rootComponentLeaveValidator.current &&
      rootComponentLeaveValidator.current(nextRoute).withoutReset
    ) {
      return false;
    }

    const shouldLeave = routerWillLeaveValidator.current(nextRoute);

    if (shouldLeave) {
      unmountCleanupMethods.current.forEach(fn => fn(nextRoute));
      return false;
    }

    return true;
  });

  const retryTransition = () => {
    if (blocker.state === 'blocked') {
      unmountCleanupMethods.current.forEach(fn => fn());
      blocker.proceed();
    }
  };

  return {
    location,
    params,
    navigate,
    setRouterWillLeaveValidator,
    retryTransition,
    registerUnmountCleanupMethod,
    setRootComponentLeaveValidator,
  };
}

export default function routeLeaveHook(WrappedComponent: any) {
  function RouteLeaveHook(props: any) {
    const routeLeaveProps = useRouteLeaveHook();

    return <WrappedComponent {...props} {...routeLeaveProps} />;
  }

  const name = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  RouteLeaveHook.displayName = `routeLeaveHook(${name})`;

  return RouteLeaveHook;
}
